import React, { useEffect, useMemo, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { getFreeTapes, updateFreeTapeStatus } from "../Redux/Reducers/ProductSlice";
import { getPaginationTotal, getRowsPerPageOptions } from "../helper/tablePagination";
import useTableUrlState from "../hook/useTableUrlState";
import { getSortOrder, sortTableData } from "../helper/tableSort";

const getField = (item, keys, fallback = "-") => {
  const value = keys
    .map((key) =>
      key.split(".").reduce((currentValue, keyPart) => currentValue?.[keyPart], item)
    )
    .find((field) => field !== undefined && field !== null && field !== "");
  return value ?? fallback;
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isDisplayableValue = (value) =>
  value !== null && value !== undefined && typeof value !== "object" && String(value).trim() !== "";

const statusOptions = [
  { label: "pending", value: 0 },
  { label: "shipped", value: 1 },
];

const normalizeStatus = (status) => {
  if (status === 1 || status === "1") return 1;
  if (String(status).toLowerCase() === "shipped") return 1;
  return 0;
};

const getStatusLabel = (status) => (normalizeStatus(status) === 1 ? "shipped" : "pending");

const getStatusColor = (status) =>
  normalizeStatus(status) === 1
    ? "bg-success-100 text-success-800"
    : "bg-warning-100 text-warning-800";

const FreeTapeList = () => {
  const dispatch = useDispatch();
  const [selectedTape, setSelectedTape] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [{ filter, page, rowsPerPage, searchText, sortName, sortDirection }, setTableState] = useTableUrlState({
    filter: "all",
    page: 0,
    rowsPerPage: 12,
    searchText: "",
    sortName: "",
    sortDirection: "",
  });
  const [isSearchUpdate, setIsSearchUpdate] = useState(false);
  const { freeTapes, loading, error, freeTapesPagination, updatingFreeTapeStatus } = useSelector((state) => state.products);
  const freeTapesTotal = getPaginationTotal(freeTapesPagination, Array.isArray(freeTapes) ? freeTapes.length : 0);
  const hasSearch = Boolean(searchText?.trim());
  const isLocalFilteredView = !hasSearch && filter !== "all";
  const requestPage = isLocalFilteredView ? 1 : page + 1;
  const requestPerPage = isLocalFilteredView
    ? Math.max(freeTapesTotal, rowsPerPage)
    : rowsPerPage;

  useEffect(() => {
    dispatch(
      getFreeTapes({
        page: requestPage,
        perPage: requestPerPage,
        search: hasSearch ? searchText : "",
      }),
    );
  }, [dispatch, hasSearch, requestPage, requestPerPage, searchText]);

  useEffect(() => {
    if (!loading) {
      setIsSearchUpdate(false);
    }
  }, [loading]);

  const tableData = useMemo(() => {
    const tapes = Array.isArray(freeTapes) ? freeTapes : [];

    if (filter !== "monthly" || hasSearch) {
      return tapes;
    }

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    return tapes.filter((tape) => {
      const tapeDate = new Date(getField(tape, ["created_at", "createdAt", "date"], ""));
      return tapeDate.getMonth() === currentMonth && tapeDate.getFullYear() === currentYear;
    });
  }, [filter, freeTapes, hasSearch]);
  const sortedTableData = useMemo(
    () =>
      sortTableData(tableData, sortName, sortDirection, {
        email: (tape) => getField(tape, ["email", "customer_email", "user.email"], ""),
        status: (tape) => getStatusLabel(tape?.status),
      }),
    [tableData, sortName, sortDirection],
  );

  const detailRows = useMemo(() => {
    if (!selectedTape) return [];

    const detailSource = {
      ...selectedTape,
      user_email: selectedTape.user?.email,
    };

    const preferredKeys = [
      "name",
      "user_email",
      "address",
      "city",
      "state",
      "zip",
      "status",
      "created_at",
    ];

    return preferredKeys
      .filter((key) => isDisplayableValue(detailSource[key]))
      .map((key) => ({
        label: key === "created_at" ? "Requested At" : formatLabel(key),
        value: key === "status" ? getStatusLabel(detailSource[key]) : key.includes("date") || key.endsWith("_at") ? formatDate(detailSource[key]) : detailSource[key],
      }));
  }, [selectedTape]);

  const handleStatusChange = async (tapeId, currentStatus, newStatus) => {
    if (normalizeStatus(currentStatus) === 1) return;

    setOpenDropdown(null);
    try {
      const response = await dispatch(updateFreeTapeStatus({ tapeId, status: newStatus })).unwrap();
      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Status Updated!",
          text: `Free tape status changed to ${getStatusLabel(newStatus)}`,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: response?.message || "Failed to update status.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error || "Something went wrong while updating status.",
      });
    }
  };

  const columns = [
    {
      name: "name",
      label: "Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedTableData[tableMeta.rowIndex];
          return (
            <div className="d-flex flex-column">
              <span className="fw-semibold">
                {getField(rowData, ["name", "full_name", "customer_name", "first_name"])}
              </span>
            </div>
          );
        },
      },
    },
    {
      name: "email",
      label: "Email",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedTableData[tableMeta.rowIndex];
          return <span>{getField(rowData, ["email", "customer_email", "user.email"], "")}</span>;
        },
      },
    },
    {
      name: "created_at",
      label: "Date",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedTableData[tableMeta.rowIndex];
          return <span>{formatDate(getField(rowData, ["created_at", "createdAt", "date"], ""))}</span>;
        },
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = sortedTableData[tableMeta.rowIndex];
          const tapeId = rowData?.id ?? rowData?.free_tape_id;
          const currentStatus = rowData?.status ?? value;
          const isShipped = normalizeStatus(currentStatus) === 1;
          const isDisabled = updatingFreeTapeStatus || isShipped;
          const isOpen = openDropdown === tapeId;

          return (
            <div className="position-relative payment-status-dropdown">
              <div
                className={`col-payment-status ${getStatusColor(currentStatus)}`}
                style={{
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  opacity: updatingFreeTapeStatus ? 0.6 : 1,
                }}
                onClick={() => !isDisabled && setOpenDropdown(isOpen ? null : tapeId)}
              >
                {updatingFreeTapeStatus && isOpen ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      style={{ width: "12px", height: "12px" }}
                    />
                    Updating...
                  </>
                ) : (
                  <>
                    {getStatusLabel(currentStatus)}
                    {!isShipped && (
                      <Icon
                        icon="mdi:chevron-down"
                        width="16"
                        height="16"
                        style={{ marginLeft: "4px", verticalAlign: "middle" }}
                      />
                    )}
                  </>
                )}
              </div>
              {isOpen && (
                <div
                  className="position-absolute"
                  style={{
                    top: "100%",
                    left: 0,
                    zIndex: 1000,
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    minWidth: "120px",
                  }}
                >
                  {statusOptions.map((status) => (
                    <div
                      key={status.value}
                      className="value"
                      style={{
                        cursor: updatingFreeTapeStatus ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        padding: "8px 12px",
                        opacity: updatingFreeTapeStatus ? 0.6 : 1,
                        pointerEvents: updatingFreeTapeStatus ? "none" : "auto",
                      }}
                      onClick={() =>
                        !updatingFreeTapeStatus && handleStatusChange(tapeId, currentStatus, status.value)
                      }
                    >
                      {status.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        },
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const rowData = sortedTableData[dataIndex];
          return (
            <div className="d-flex justify-content-center">
              <Icon
                className="editBtn"
                icon="boxicons:eye"
                width="24"
                height="24"
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedTape(rowData)}
              />
            </div>
          );
        },
      },
    },
  ];

  const tableTotal = isLocalFilteredView ? sortedTableData.length : freeTapesTotal;

  useEffect(() => {
    if (page > 0 && tableTotal <= page * rowsPerPage) {
      setTableState({ page: 0 });
    }
  }, [page, rowsPerPage, setTableState, tableTotal]);

  const options = {
    selectableRows: "none",
    rowsPerPage,
    rowsPerPageOptions: getRowsPerPageOptions(tableTotal),
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    sortOrder: getSortOrder(sortName, sortDirection),
    onColumnSortChange: (changedColumn, direction) => {
      setTableState({
        sortName: direction === "none" ? "" : changedColumn,
        sortDirection: direction === "none" ? "" : direction,
      });
    },
    searchText,
    onSearchChange: (searchValue) => {
      setIsSearchUpdate(true);
      setTableState({ searchText: searchValue || "", page: 0 });
    },
    serverSide: !isLocalFilteredView,
    count: tableTotal,
    page,
    onChangePage: (newPage) => {
      setTableState({ page: newPage });
    },
    onChangeRowsPerPage: (numberOfRows) => {
      setTableState({ rowsPerPage: numberOfRows, page: 0 });
    },
  };

  if (loading && !isSearchUpdate) {
    return (
      <div className="card packageTable">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 mb-0">Loading free tape requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card packageTable">
        <div className="card-body text-center py-5">
          <div className="text-danger">
            <Icon icon="material-symbols:error-outline" width="48" height="48" />
            <p className="mt-3">
              Error: {typeof error === "string" ? error : "Failed to load free tape requests"}
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() =>
                dispatch(
                  getFreeTapes({
                    page: requestPage,
                    perPage: requestPerPage,
                    search: hasSearch ? searchText : "",
                  }),
                )
              }
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card packageTable free-tape-table">
        <div className="card-header d-flex justify-content-end align-items-center">
          <div className="custom-toggle">
            <div className="toggle-container">
              <div className={`toggle-indicator ${filter === "all" ? "right" : "left"}`} />
              <div
                className={`toggle-option ${filter === "monthly" ? "active" : ""}`}
                onClick={() => setTableState({ filter: "monthly", page: 0 })}
              >
                <Icon icon="mdi:calendar-month" width="22" />
                <span>This Month</span>
              </div>
              <div
                className={`toggle-option ${filter === "all" ? "active" : ""}`}
                onClick={() => setTableState({ filter: "all", page: 0 })}
              >
                <Icon icon="mdi:calendar-today" width="25" />
                <span>All Time</span>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          <MUIDataTable
            data={sortedTableData}
            columns={columns}
            options={options}
            className="overflow-hidden ordersTable"
          />
        </div>
      </div>

      {selectedTape && (
        <div className="modal fade show d-block free-tape-detail-modal" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Free Tape Details</h5>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {detailRows.map((row) => (
                    <div className="col-md-6" key={row.label}>
                      <div className="free-tape-detail-item">
                        <span>{row.label}</span>
                        <strong>{String(row.value)}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="designBtn" onClick={() => setSelectedTape(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setSelectedTape(null)} />
        </div>
      )}
    </>
  );
};

export default FreeTapeList;
